import { arrayUtils } from "../../helpers/arrayUtils";
import {
  BodyContentType,
  DefaultConfigs,
  DefaultValues,
  RequestMethod,
  SourceType,
  BodyType,
  ResponseMode,
} from "../constants/restRequestConstants";
import { envVarProcessor } from "./envVarProcessor";

function _createArrayOperations(propName, defaultItemTemplate) {
  const operations = {
    addNew: (request) => {
      request[propName] = arrayUtils.ensureArray(request[propName]);
      arrayUtils.add(request[propName], { ...defaultItemTemplate });
    },

    add: (request, key, value) => {
      request[propName] = arrayUtils.ensureArray(request[propName]);
      arrayUtils.add(request[propName], {
        ...defaultItemTemplate,
        key,
        value,
      });
    },

    remove: (request, index) => {
      arrayUtils.remove(request[propName], index);
    },

    clear: (request) => {
      request[propName] = arrayUtils.clear(request[propName]);
    },
  };

  return operations;
}

const requestOperations = {
  header: _createArrayOperations("headers", DefaultConfigs.header),
  queryParam: _createArrayOperations("queryParams", DefaultConfigs.queryParam),
  envVar: _createArrayOperations("envVars", DefaultConfigs.envVar),
  mapping: _createArrayOperations("mappings", DefaultConfigs.mapping),

  // For `urlEncoded` and `formData`, make sure to pass `request.body` instead of `request` as the parameter when calling `add`, `addNew`, etc.
  urlEncoded: _createArrayOperations("urlEncoded", DefaultConfigs.urlEncoded),
  formData: _createArrayOperations("formData", DefaultConfigs.formData),
};

function resolveFieldValue(item, questions, isDesignMode) {
  if (!item?.enabled) {
    return "";
  }

  switch (item.source) {
    case SourceType.FIELD:
      return _resolveFieldFromQuestions(item, questions, isDesignMode);
    case SourceType.CREDENTIAL:
      return {
        isCredential: true,
        credentialId: item.credentialId,
        credentialField: item.credentialField
      };
    case SourceType.SERVER_VARIABLE:
      if (isDesignMode && item.testValue) {
        return item.testValue;
      }
      return `<<<SV_START>>>${item.serverVariableId}<<<SV_END>>>`;

    case SourceType.FIXED:
    default:
      return item.value;
  }
}

function _resolveFieldFromQuestions(item, questions, isDesignMode) {

  if (isDesignMode && item.testValue) {
    return item.testValue;
  }

  if (!item.fieldId) {
    return "";
  }

  const field = questions.find((q) => q.ClientID === item.fieldId);

  return field?.Answer || `{{${item.fieldId}}}`;
}

function _resolveParameters(params, questions, isDesignMode) {
  return (params || [])
    .filter((p) => p.enabled)
    .reduce((acc, param) => {
      if (param.source === SourceType.CREDENTIAL) {
        if (isDesignMode && param.testValue) {
          acc.push({
            enabled: param.enabled,
            key: param.key,
            value: param.testValue,
          });
        } else {
          acc.push({
            enabled: param.enabled,
            key: param.key,
            value: param.credentialId,
            credentialField: param.credentialField,
            type: SourceType.CREDENTIAL,
          });
        }
      } else {
        const value = resolveFieldValue(param, questions, isDesignMode);
        if (value) {
          acc.push({
            enabled: param.enabled,
            key: param.key,
            value,
          });
        }
      }
      return acc;
    }, []);
}

function setBodyContentType(request, contentType) {
  request.body.contentType = contentType;

  switch (contentType) {
    case BodyContentType.FORM:
      request.body.urlEncoded = arrayUtils.ensureArray(request.body.urlEncoded);
      break;
    case BodyContentType.MULTIPART:
      request.body.formData = arrayUtils.ensureArray(request.body.formData);
      break;
  }
}

async function _resolveFormData(
  clonedRequest,
  questions,
  isDesignMode = false
) {
  if (!clonedRequest?.body?.formData?.length) {
    return [];
  }

  const multipartFormData = new FormData();

  // Use to store the form data items in the format 
  // expected by the request proxy service in api-integrations
  const formData = [];

  // Then handle each form data item
  for (const item of clonedRequest.body.formData.filter(i => i.enabled)) {
    switch (item.source) {
      case SourceType.FIXED:
      case SourceType.SERVER_VARIABLE:
        formData.push({
          key: item.key,
          value: item.value,
          type: 'text',
          enabled: true,
        })
        break;

      case SourceType.FIELD:
        if (isDesignMode && item.testValue) {
          formData.push({
            key: item.key,
            value: item.testValue,
            type: 'text',
            enabled: true,
          });
        } else {
          const fieldValue = resolveFieldValue(item, questions, isDesignMode);
          if (fieldValue) {
            formData.push({
              key: item.key,
              value: fieldValue,
              type: 'text',
              enabled: true,
            });
          }
        }

        break;

      case SourceType.ATTACHMENT:
        if (isDesignMode) {
          if (item.testFile) {
            formData.push({
              key: item.key,
              type: 'file',
              enabled: true,
            });

            multipartFormData.append(item.key, item.testFile, item.testFile.name);
          }
        } else {
          // Runtime mode - get file from attachment field
          const field = questions.find(q => q.ClientID === item.fieldId);
          const fileIDs = field?.Answer?.map(a => a.file_key) || [];

          if (field?.Answer?.[0]) {

            formData.push({
              key: item.key,
              type: 'file',
              enabled: true,

              // Files will be downloaded from the file service
              // in api-integrations
              attachments: fileIDs,
            });
          }
        }

        break;

      case SourceType.CREDENTIAL:
        if (isDesignMode && item.testValue) {
          formData.push({
            key: item.key,
            value: item.testValue,
            type: 'text',
            enabled: true,
          });
        } else {
          formData.push({
            key: item.key,
            value: item.credentialId,
            credentialField: item.credentialField,
            type: SourceType.CREDENTIAL,
            enabled: true,
          })
        }
        break;
    }
  }

  clonedRequest.body.formData = formData;

  multipartFormData.append("body", JSON.stringify(clonedRequest));

  return multipartFormData;
}

async function prepareRequest(
  request,
  questions,
  isDesignMode
) {

  // Use structuredClone to deep clone while preserving File objects
  const preparedRequest = structuredClone(request);

  _applyBackwardCompatibilityChanges(preparedRequest);

  if (!isDesignMode && preparedRequest?.response?.responseMode === ResponseMode.TEST) {
    // TEST mode is only used in design mode
    console.warn(
      "TEST mode can only used in design mode. " +
      "Changing mode to STANDARD."
    );

    preparedRequest.response.responseMode = ResponseMode.STANDARD;
  }

  // Set proper content type based on body type
  if (preparedRequest.body?.type) {
    switch (preparedRequest.body.type) {
      case BodyType.RAW:
        preparedRequest.body.contentType = preparedRequest.body.contentType || BodyContentType.JSON;
        break;
      case BodyType.FORM:
        preparedRequest.body.contentType = BodyContentType.FORM;
        break;
      case BodyType.MULTIPART:
        preparedRequest.body.contentType = BodyContentType.MULTIPART;
        break;
      case BodyType.NONE:
        delete preparedRequest.body.contentType;
        break;
    }
  }

  preparedRequest.headers = _resolveParameters(preparedRequest.headers, questions, isDesignMode);
  preparedRequest.queryParams = _resolveParameters(preparedRequest.queryParams, questions, isDesignMode);

  if (preparedRequest.body.urlEncoded) {
    preparedRequest.body.urlEncoded = _resolveParameters(
      preparedRequest.body.urlEncoded,
      questions,
      isDesignMode
    );
  }

  envVarProcessor.resolveEnvironmentVariables(preparedRequest, questions, isDesignMode);

  // Remove envVars from the request body as it's not needed
  // by the request proxy service
  delete preparedRequest.envVars;

  const supportsFormData = [RequestMethod.POST, RequestMethod.PUT, RequestMethod.PATCH].includes(preparedRequest.method);
  const hasFormData = preparedRequest.body?.formData?.length > 0;

  if (hasFormData && supportsFormData && preparedRequest.body.type === BodyType.MULTIPART) {
    const formData = await _resolveFormData(
      preparedRequest,
      questions,
      isDesignMode
    );

    // Clean up test files after form data is prepared, but only if not in design mode
    if (!isDesignMode && preparedRequest.body?.formData) {
      preparedRequest.body.formData.forEach(item => {
        delete item.testFile;
      });
    }

    return formData;
  }

  // If not multipart/form-data, don't send formData
  delete preparedRequest.body.formData;

  return preparedRequest;
}

// For backward compatibility
// Added followRedirects, maxRedirects, persistCookies, responseMode, mapTo, serverVariableExpiry and serverVariable to the request object after v1
function _applyBackwardCompatibilityChanges(preparedRequest) {

  if (!preparedRequest.hasOwnProperty('followRedirects') || preparedRequest.followRedirects === undefined) {
    preparedRequest.followRedirects = true;
  }

  if (!preparedRequest.hasOwnProperty('maxRedirects') || preparedRequest.maxRedirects === undefined) {
    preparedRequest.maxRedirects = DefaultValues.MAX_REDIRECTS;
  }

  if (!preparedRequest.hasOwnProperty('persistCookies') || preparedRequest.persistCookies === undefined) {
    preparedRequest.persistCookies = false;
  }

  if (preparedRequest.mappings) {
    preparedRequest.mappings.forEach((mapping) => {
      if (!mapping.mapTo) {
        mapping.mapTo = SourceType.FIELD;
      }
    });
  }

  if (!preparedRequest.response?.responseMode) {

    if (!preparedRequest.response) {
      preparedRequest.response = {};
    }

    preparedRequest.response.responseMode = ResponseMode.STANDARD;
  }
}


export const restRequestUtils = {
  requestOperations,
  setBodyContentType,
  prepareRequest,
  resolveFormData: _resolveFormData,
  resolveFieldValue,
};
